// Reporting for the errors an operator reads in a banner.
//
// The screens' idiom is `catch (error) { setActionError(error.message) }`. That
// string reaches the DOM and nothing else, so a failure the operator was
// blocked by left no trace anywhere — the conflict that started this work was
// reported to us verbally, by a human reading a red box.
//
// This is UI policy, not transport, which is why it is not part of
// `lib/telemetry.ts`: that module is the SDK and one of only two files exempt
// from the `network.guard.test.ts` fetch ban. Nothing here opens a socket.
//
// ── The dedupe rule ─────────────────────────────────────────────────────────
// An error is reported by exactly ONE layer. `api/client.ts` owns every error
// it produces and stamps all three of its throws with `markReported` —
// including the ones it deliberately keeps quiet about (aborts, GET 401s). So
// this function reports only what nothing else has: client-side throws.
//
// The stamp, rather than `instanceof ApiError` alone, is what catches the raw
// `TypeError` a network failure rethrows — already captured upstream as
// `api_transient`. The `instanceof` check stays as well, so that an `ApiError`
// minted or re-wrapped outside the client cannot silently double-log.

import { ApiError } from '@/api/errors';
import { captureError, describeError, stackOf, typeTag, wasReported } from '@/lib/telemetry';

/** A failure caused by what the operator typed, not by the system.
 *
 *  Thrown by the wizard's field guards so this module can tell validation from
 *  breakage *by construction* rather than by matching message text. Owner
 *  decision: these are not logged. */
export class OperatorInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OperatorInputError';
  }
}

export function operatorInputError(message: string): OperatorInputError {
  return new OperatorInputError(message);
}

export interface ReportActionErrorOptions {
  /** Stable machine label for the attempted action, e.g. `campaign.save_draft`.
   *
   *  Set as `context.operation` AND prefixed onto the logged message, because
   *  `message` is the only field that survives every projection: the triage
   *  reader allowlists context keys, and the ones this SDK emits are not on it. */
  operation: string;
  /** Banner text when the error carries no message of its own. Affects the
   *  banner only — the log line always keeps the diagnostic string. */
  fallback?: string;
  context?: Record<string, unknown>;
}

/** Report `error` unless another layer already did, and return the string to
 *  hand to `setActionError`. Never throws; never alters control flow. */
export function reportActionError(error: unknown, opts: ReportActionErrorOptions): string {
  const detail = describeError(error, 'Unknown error');
  const own = (error as { message?: unknown })?.message;
  const banner = (typeof own === 'string' && own.trim() && own)
    || opts.fallback
    || detail;

  const suppressed = error instanceof OperatorInputError
    || (error as { name?: unknown })?.name === 'AbortError'
    || wasReported(error)
    || error instanceof ApiError;

  if (!suppressed) {
    captureError({
      level: 'error',
      error_kind: 'action_failed',
      message: `${opts.operation}: ${detail}`,
      stack: stackOf(error),
      context: {
        operation: opts.operation,
        error_kind: typeTag(error),
        ...(opts.context ?? {}),
      },
    });
  }

  return banner;
}
